export const colorNames = ['GRAY', 'BLUE', 'GREEN', 'YELLOW', 'ORANGE', 'RED', 'PINK', 'PURPLE'] as const

export type ColorName = (typeof colorNames)[number]

export interface ColorSet {
  /** For canvas backgrounds. */
  bg: string
  /** For text, whether on the `bg` color or on default `canvas` colors. */
  fg: string
  /** For borders around the `bg` color. */
  border: string
  /** For icons and standalone visuals where `fg` is too dark. */
  accent: string
}

function roleColorSet(role: string): ColorSet {
  return {
    bg: `var(--bgColor-${role}-muted)`,
    fg: `var(--fgColor-${role})`,
    border: `var(--borderColor-${role}-muted)`,
    accent: `var(--bgColor-${role}-emphasis)`,
  }
}

const colorSets: Record<ColorName, ColorSet> = {
  GRAY: {
    bg: 'var(--bgColor-muted)',
    fg: 'var(--fgColor-muted)',
    border: 'var(--borderColor-default)',
    accent: 'var(--fgColor-muted)',
  },
  BLUE: roleColorSet('accent'),
  GREEN: roleColorSet('success'),
  YELLOW: roleColorSet('attention'),
  ORANGE: roleColorSet('severe'),
  RED: roleColorSet('danger'),
  PINK: roleColorSet('sponsors'),
  PURPLE: roleColorSet('done'),
}

// eslint-disable-next-line @eslint-react/no-unnecessary-use-prefix -- public API name used by many callers
export const useNamedColor = (name: ColorName = 'GRAY'): ColorSet => {
  return colorSets[name] ?? colorSets.GRAY
}

const getPresentationalColor = (name: ColorName = 'GRAY'): ColorSet => colorSets[name] ?? colorSets.GRAY

// eslint-disable-next-line @eslint-react/no-unnecessary-use-prefix -- public API name used by many callers
export const useGetPresentationalColor = () => {
  return {getPresentationalColor}
}

/** Helper to validate if a string is an acceptable color name (case sensitive). */
export const isColorName = (str: string): str is ColorName => (colorNames as readonly string[]).includes(str)
